import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './ApproachSection.css';

/* ── Icons ── */
const IconCode = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);
const IconApi = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);
const IconLightning = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const approaches = [
  {
    id: 1,
    icon: <IconCode />,
    title: 'Чистый код',
    tagline: 'Читаемость прежде всего',
    color: '#5227ff',
    description: 'Пишу код так, чтобы его было легко читать и поддерживать. Следую принципам SOLID и чистой архитектуры — не ради буквы закона, а ради реального удобства работы с кодом.',
    points: [
      'Понятные имена переменных и функций',
      'Разделение ответственности (SRP)',
      'Минимальная связанность модулей',
      'Документирование нетривиальных решений',
    ],
  },
  {
    id: 2,
    icon: <IconApi />,
    title: 'API First',
    tagline: 'Контракт до реализации',
    color: '#7c4dff',
    description: 'Начинаю проектирование с API — определяю контракты и структуры данных до написания бизнес-логики. Это ускоряет параллельную разработку и снижает количество переделок.',
    points: [
      'OpenAPI / Swagger спецификации',
      'Версионирование и обратная совместимость',
      'Единообразные ответы и коды ошибок',
      'Документация как часть продукта',
    ],
  },
  {
    id: 3,
    icon: <IconLightning />,
    title: 'Производительность',
    tagline: 'Оптимизация там, где важно',
    color: '#CCFF00',
    description: 'Профилирую узкие места вместо преждевременной оптимизации. Работаю на уровне БД, кода и инфраструктуры — там, где изменения дают реальный эффект.',
    points: [
      'Индексы и оптимизация SQL-запросов',
      'Кэширование (Redis, in-memory)',
      'Асинхронная обработка задач',
      'Мониторинг метрик производительности',
    ],
  },
  {
    id: 4,
    icon: <IconShield />,
    title: 'Надёжность',
    tagline: 'Системы, которые выдерживают',
    color: '#00e5ff',
    description: 'Проектирую с учётом отказоустойчивости. Логирование, мониторинг и обработка ошибок — не опция, а базовое требование к любому сервису в продакшне.',
    points: [
      'Graceful degradation при сбоях',
      'Structured logging и трассировка',
      'Health checks и алерты',
      'Retry-логика и circuit breakers',
    ],
  },
  {
    id: 5,
    icon: <IconLayers />,
    title: 'Масштабируемость',
    tagline: 'Рост без архитектурного долга',
    color: '#ff6b6b',
    description: 'Строю сервисы, которые горизонтально масштабируются без переписывания. Использую очереди сообщений и микросервисную архитектуру там, где это оправдано.',
    points: [
      'Stateless сервисы, общий кэш',
      'Message queues (Kafka, RabbitMQ)',
      'Docker и контейнерная оркестрация',
      'Database sharding и репликация',
    ],
  },
  {
    id: 6,
    icon: <IconLock />,
    title: 'Безопасность',
    tagline: 'Встроена в процесс с первого дня',
    color: '#ff9f43',
    description: 'Безопасность — не дополнительный слой поверх готового сервиса, а часть проектирования. Валидация, аутентификация и авторизация продуманы на этапе архитектуры.',
    points: [
      'JWT / OAuth2 аутентификация',
      'Валидация и санитизация данных',
      'Rate limiting и защита от брутфорса',
      'Шифрование чувствительных данных',
    ],
  },
];

export default function ApproachSection() {
  const [active, setActive] = useState(null);

  return (
    <div className="approach-root">
      <div className="approach-grid">
        {approaches.map((item) => (
          <button
            key={item.id}
            className="approach-card cursor-target"
            onClick={() => setActive(item)}
            style={{ '--accent': item.color }}
            type="button"
          >
            <span className="approach-card-icon" style={{ color: item.color }}>
              {item.icon}
            </span>
            <h3 className="approach-card-title">{item.title}</h3>
            <p className="approach-card-tagline">{item.tagline}</p>
            <span className="approach-card-arrow">→</span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="approach-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setActive(null)}
          >
            <motion.div
              className="approach-modal"
              initial={{ opacity: 0, scale: 0.93, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 16 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              style={{ '--accent': active.color }}
            >
              <button
                className="approach-modal-close"
                onClick={() => setActive(null)}
                type="button"
                aria-label="Закрыть"
              >
                ✕
              </button>

              <div className="approach-modal-header">
                <div className="approach-modal-icon-box" style={{ color: active.color }}>
                  {active.icon}
                </div>
                <div>
                  <h2 className="approach-modal-title">{active.title}</h2>
                  <p className="approach-modal-tagline">{active.tagline}</p>
                </div>
              </div>

              <div className="approach-modal-divider" />

              <p className="approach-modal-desc">{active.description}</p>

              <ul className="approach-modal-points">
                {active.points.map((pt, i) => (
                  <li key={i} className="approach-modal-point">
                    <span
                      className="approach-modal-bullet"
                      style={{ background: active.color }}
                    />
                    {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
